/**
 * Recargos de factura (espejo de api/src/shared/billing/invoiceSurcharges.ts) para mostrar en vivo
 * cómo queda la factura mientras se edita. El servidor es el que guarda: rearma estas líneas siempre.
 */

export type SurchargeTipo = 'mora' | 'reconexion';

export type InvoiceSurcharge = {
  tipo: SurchargeTipo;
  porcentaje: number;
  monto: number;
  origen: 'auto' | 'manual';
  aplicadoAt: string;
  aplicadoPor: string | null;
  quitadoAt: string | null;
  quitadoPor: string | null;
  motivoQuitado: string | null;
};

export type SurchargeExemption = {
  motivo: string;
  createdAt: string;
  createdBy: string | null;
};

type Line = {
  kind: string;
  name: string;
  description: string | null;
  quantity: number;
  unitPrice: number;
  subtotal: number;
};

export function isSurchargeKind(kind: string | null | undefined): boolean {
  return kind === 'recargo_mora' || kind === 'reconexion';
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Líneas de recargo vigentes sobre las líneas base (mora sobre la base; reconexión sobre base + mora). */
export function buildSurchargeLines<T extends Line>(baseLines: T[], surcharges: InvoiceSurcharge[] | undefined): T[] {
  const active = new Map((surcharges ?? []).filter((s) => !s.quitadoAt).map((s) => [s.tipo, s.porcentaje]));
  const base = round2(Math.max(0, baseLines.filter((l) => !isSurchargeKind(l.kind)).reduce((s, l) => s + l.subtotal, 0)));
  const moraPct = active.get('mora');
  const recPct = active.get('reconexion');
  const mora = moraPct ? round2((base * moraPct) / 100) : 0;
  const rec = recPct ? round2(((base + mora) * recPct) / 100) : 0;
  const out: T[] = [];
  if (moraPct && mora > 0) {
    out.push({ kind: 'recargo_mora', name: `Recargo por mora (${moraPct}%)`, description: null, quantity: 1, unitPrice: mora, subtotal: mora } as T);
  }
  if (recPct && rec > 0) {
    out.push({ kind: 'reconexion', name: `Costo de reconexión (${recPct}%)`, description: null, quantity: 1, unitPrice: rec, subtotal: rec } as T);
  }
  return out;
}

/** Porcentaje guardado en el nombre de la línea ("Recargo por mora (15%)"). */
export function surchargePctFromName(name: string): number | null {
  const m = /\((\d+(?:[.,]\d+)?)%\)/.exec(name);
  return m ? Number(m[1].replace(',', '.')) : null;
}
